import React, { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, SendHorizonal, User } from "lucide-react";
import { API } from "@/lib/api";

export default function AIAssistant({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    { role: "ai", text: "Hi! I'm your MIS assistant. Ask me about projects, cashflow, EVM, pipeline or overdue collections." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef("sess_" + Math.random().toString(36).slice(2, 10));
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 9999, behavior: "smooth" });
  }, [messages, busy]);

  const suggestions = [
    "What is our overdue collection amount?",
    "Which project has the worst CPI?",
    "Summarize this month's payroll cost.",
    "Give me top 3 pipeline deals.",
  ];

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "ai", text: "" }]);
    setBusy(true);
    try {
      const token = localStorage.getItem("session_token");
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      if (!resp.ok || !resp.body) throw new Error("AI request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "ai", text: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "ai", text: "Sorry, I couldn't reach the AI service." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="flex items-center gap-2 font-display">
            <Sparkles className="h-4 w-4 text-secondary" />
            AI Assistant
            <span className="ml-auto text-[10px] text-muted-foreground font-mono uppercase">Claude Sonnet 4.5</span>
          </SheetTitle>
        </SheetHeader>

        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${
                m.role === "user" ? "bg-secondary/40 text-secondary-foreground" : "bg-primary text-primary-foreground"
              }`}>
                {m.role === "user" ? <User className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              </div>
              <div className={`text-sm leading-relaxed rounded-md px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                m.role === "user" ? "bg-accent" : "bg-muted"
              }`}>
                {m.text || (busy && i === messages.length - 1 ? "…" : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 border-t bg-card space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={busy}
                data-testid={`ai-suggestion-${s.slice(0,10)}`}
                className="text-[11px] px-2 py-1 rounded border bg-background hover:bg-accent transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask about finance, projects, leads, aging…"
              className="min-h-[44px] max-h-32 resize-none text-sm"
            />
            <Button
              onClick={() => send()}
              data-testid="ai-send"
              disabled={busy || !input.trim()}
              className="h-11 bg-primary hover:bg-primary/90"
            >
              <SendHorizonal className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
